import { useLocation } from 'react-router-dom';
import { useOrganization, useSession, useUser } from '@clerk/clerk-react';
import { useMemo } from 'react';
import { useAssistantContext, useAssistantInstructions } from '@assistant-ui/react';
import { useDashboardAssistantActions } from '../../hooks/useDashboardAssistantActions';
import { getCustomRole, getEffectiveRole, getUserPermissions } from '../../utils/auth';
import {
  formatTimestampWithTimezoneAndTime,
  getTimezoneOffset,
  getValidTimezone,
} from '../../utils/formatTimestamp';

const PATH_FEATURE_MAP = [
  { prefix: '/settings', label: 'Settings' },
  { prefix: '/payments', label: 'Payments' },
  { prefix: '/contacts', label: 'Contacts' },
  { prefix: '/subscriptions', label: 'Subscriptions' },
  { prefix: '/forms', label: 'Forms' },
  { prefix: '/elements', label: 'Elements' },
  { prefix: '/payouts', label: 'Payouts' },
  { prefix: '/dashboard', label: 'Dashboard Overview' },
  { prefix: '/onboarding', label: 'Onboarding' },
  { prefix: '/signin', label: 'Sign In' },
  { prefix: '/signup', label: 'Sign Up' },
];

function getCurrentFeature(pathname) {
  if (pathname === '/') {
    return 'Home';
  }

  const matchedFeature = PATH_FEATURE_MAP.find(({ prefix }) => pathname.startsWith(prefix));
  return matchedFeature?.label || 'Dashboard';
}

function getActiveMembership(clerkUser, organizationId) {
  const memberships = clerkUser?.organizationMemberships || [];

  if (organizationId) {
    return memberships.find((membership) => membership.organization.id === organizationId) || null;
  }

  return memberships[0] || null;
}

function getMembershipOrganization(membership) {
  return membership?.organization || membership?.publicOrganizationData || null;
}

function formatOrganizationSlug(slug) {
  return slug ? slug.replace(/-/g, ' ') : undefined;
}

function getActiveOrganizationContext({
  memberships,
  organization,
  organizationNames,
  sessionOrganizationId,
}) {
  const singleMembershipOrganization =
    memberships.length === 1 ? getMembershipOrganization(memberships[0]) : null;
  const singleOrganizationName =
    Array.isArray(organizationNames) && organizationNames.length === 1
      ? organizationNames[0]
      : undefined;
  const organizationId =
    organization?.id || sessionOrganizationId || singleMembershipOrganization?.id;
  const membershipOrganization = organizationId
    ? memberships
        .map(getMembershipOrganization)
        .find((membershipOrg) => membershipOrg?.id === organizationId)
    : singleMembershipOrganization;
  const organizationName =
    organization?.name ||
    membershipOrganization?.name ||
    formatOrganizationSlug(organization?.slug) ||
    formatOrganizationSlug(membershipOrganization?.slug) ||
    singleOrganizationName;

  return {
    organizationId,
    organizationName,
  };
}

function normalizeRole(role) {
  return role ? role.replace(/^org:/, '') : undefined;
}

function getUserState(pathname, organizationCount) {
  if (pathname.startsWith('/onboarding')) {
    return 'onboarding';
  }

  if (organizationCount === 0) {
    return 'pending_organization';
  }

  return undefined;
}

function DashboardAssistantRuntime({ dashboardTimezone, organizationNames = [] }) {
  const location = useLocation();
  const { user: clerkUser } = useUser();
  const { organization } = useOrganization();
  const { session } = useSession();
  const runtimeMode = 'live_browser';

  const memberships = clerkUser?.organizationMemberships || [];
  const { organizationId, organizationName } = getActiveOrganizationContext({
    memberships,
    organization,
    organizationNames,
    sessionOrganizationId: session?.lastActiveOrganizationId,
  });
  const activeMembership = getActiveMembership(clerkUser, organizationId);
  const normalizedRole = normalizeRole(activeMembership?.role);
  const effectiveRole = getEffectiveRole() || normalizedRole;
  const customRole = getCustomRole();
  const permissionKey = getUserPermissions().join('|');
  const currentFeature = getCurrentFeature(location.pathname);
  const normalizedDashboardTimezone = useMemo(
    () =>
      dashboardTimezone
        ? getValidTimezone(dashboardTimezone, 'America/Chicago')
        : 'America/Chicago',
    [dashboardTimezone],
  );
  const dashboardTimezoneOffset = useMemo(
    () => getTimezoneOffset(normalizedDashboardTimezone),
    [normalizedDashboardTimezone],
  );
  const dashboardCurrentTime = useMemo(
    () =>
      formatTimestampWithTimezoneAndTime(
        new Date().toISOString(),
        normalizedDashboardTimezone,
      ),
    [normalizedDashboardTimezone],
  );
  const userState = getUserState(location.pathname, memberships.length);
  const userContext = useMemo(
    () => ({
      userId: clerkUser?.id,
      name: clerkUser?.fullName || clerkUser?.firstName || undefined,
      email: clerkUser?.primaryEmailAddress?.emailAddress || undefined,
      role: normalizedRole,
      effectiveRole,
      customRole: customRole || undefined,
      permissions: permissionKey ? permissionKey.split('|') : [],
      organizationId,
      organizationName,
      hasOrganization: memberships.length > 0,
      userState,
      dashboardTimezone: normalizedDashboardTimezone,
      dashboardTimezoneOffset,
      dashboardCurrentTime,
    }),
    [
      clerkUser?.firstName,
      clerkUser?.fullName,
      clerkUser?.id,
      clerkUser?.primaryEmailAddress?.emailAddress,
      memberships.length,
      effectiveRole,
      customRole,
      normalizedRole,
      permissionKey,
      organizationId,
      organizationName,
      userState,
      normalizedDashboardTimezone,
      dashboardTimezoneOffset,
      dashboardCurrentTime,
    ],
  );
  const routeContext = useMemo(
    () => ({
      currentFeature,
      pathname: location.pathname,
      search: location.search || undefined,
    }),
    [currentFeature, location.pathname, location.search],
  );

  useDashboardAssistantActions({
    currentFeature,
    dashboardTimezone: normalizedDashboardTimezone,
    organizationId,
    organizationName,
    routeContext,
    userContext,
  });

  const assistantContext = useMemo(
    () =>
      [
        'Current dashboard user and organization context:',
        JSON.stringify(userContext, null, 2),
        'Current dashboard route context:',
        JSON.stringify(routeContext, null, 2),
        'Current assistant runtime mode and tool capability:',
        JSON.stringify(
          {
            mode: runtimeMode,
            canNavigateDashboard: true,
            canSearchDashboardRecords: true,
            description:
              'The assistant is running inside an active browser dashboard session. Registered frontend tools can search dashboard records and navigate the current dashboard tab.',
          },
          null,
          2,
        ),
      ].join('\n\n'),
    [routeContext, runtimeMode, userContext],
  );

  const assistantInstructions = useMemo(
    () =>
      [
        'You are the CharityStack dashboard assistant.',
        'You are in live browser runtime mode. When the user asks to find, search, show, open, or navigate to dashboard records or pages, use the registered dashboard tools instead of giving generic manual instructions.',
        organizationName || organizationId
          ? `The active dashboard organization is ${organizationName ? `"${organizationName}"` : 'unknown name'}${organizationId ? ` with Clerk organization ID ${organizationId}` : ''}. Treat dashboard searches, record lookups, and navigation as scoped to this active organization.`
          : 'No active dashboard organization is available yet. Ask the user to select an organization before searching records or answering organization-specific questions.',
        'If a tool returns matching records, use those exact results and do not repeat the same tool call unless the user asks for a fresh search.',
        'For exact first donation date, last donation date, donation count, or lifetime giving questions, use get_contact_donation_summary and do not infer those facts from recent payment search results.',
        `When mentioning dates or times, use the dashboard timezone ${normalizedDashboardTimezone}${dashboardTimezoneOffset ? ` (${dashboardTimezoneOffset})` : ''}. The current dashboard time is ${dashboardCurrentTime}. Prefer formatted date fields returned by tools over raw ISO timestamps.`,
        'Only answer questions about CharityStack, nonprofit fundraising operations inside CharityStack, and CharityStack dashboard workflows.',
        'Help nonprofit administrators understand dashboard workflows, records, forms, payments, contacts, subscriptions, payouts, settings, integrations, user login, compliance, and onboarding.',
        'Use registered dashboard tools when the user asks to search, inspect context, navigate, or open a dashboard area.',
        'When the user asks for specific dashboard data, such as latest payout, latest payment, record status, amount, date, or donor details, prefer a data lookup/search tool over a page navigation tool. Navigate only when the user asks to open, go to, or view the workspace itself.',
        'When the user asks aggregate, analytical, trend, ranking, comparison, or visualization questions about payments, revenue, payment counts, average payments, recurring versus one-time giving, top forms, top funds, top contacts, top tickets, payment methods, checkout bumps, monthly bump conversion, payment statuses, payment issues, form types, or UTM attribution, use the registered analytics tool and answer from its result. Do not estimate analytics from record search results.',
        'For analytics visualization, use auto unless the user explicitly asks for table, line, bar, pie, or KPI. Appropriate defaults: revenue/payment count/average payment/payment issues over time use a line chart; total single-number questions use a KPI; top forms, top funds, top contacts, top tickets, payment methods, form types, UTM breakdowns, and payment status breakdowns use a bar chart; recurring versus one-time share uses a pie chart; checkout bump amount uses a bar chart; checkout bump attach rate and monthly bump conversion rate use a KPI; explicit table requests use a table.',
        'When the user wants to TAG contacts based on criteria (for example "tag everyone who donated 3+ times this year", "tag contacts who gave under $50 this year", or "remove the Lapsed tag from anyone who gave recently"), the flow is: (1) call resolve_contact_selection to resolve the ad-hoc selection — a preview card with the full reviewable contact table (checkboxes to exclude) is shown automatically; (2) in your reply, state ONLY the match count and restate the criteria, then ask the user to review the table and confirm — NEVER list individual contacts (no names, emails, phones, no bullet lists) in your text, the table already shows them; (3) once the user confirms in chat which tag to apply (or to create), call apply_contact_tags — pass the selectionToken from the resolve result if you have it, otherwise omit it and the most recent selection in this conversation is used. Contacts the user unchecked in the table are excluded automatically.',
        'apply_contact_tags is a REAL write: it tags the resolved contacts (minus any the user unchecked) and can create a brand-new tag in the organization dictionary when you pass createIfMissing: true. NEVER call it before the user has seen the preview and explicitly confirmed. IMPORTANT: once a preview has been shown in this conversation, a user message that names a tag and asks to apply or create it ("apply Repeat Donor to these contacts", "create a Loyal Donor tag and apply it", "yes, go ahead") IS the confirmation — call apply_contact_tags immediately; do NOT re-run resolve_contact_selection and do NOT ask again. Omit selectionToken (the most recent selection in this conversation is used automatically); never invent a token. If the tool replies that the selection expired, immediately re-run resolve_contact_selection with the same criteria in the same turn, show the refreshed preview, and ask the user to confirm once more. After a successful apply, report the exact counts it returns. Use list_contact_tags when you need to know which tags already exist or to match the user\'s wording to an existing tag name.',
        'Help the user figure out the tagging intent: are they applying an EXISTING tag, or do they want a NEW tag for this pattern? If they named a specific tag, use that one (check list_contact_tags if unsure it exists). If the request describes a group but names no clear existing tag (e.g. "tag donors who gave 3+ times this year", "flag lapsed 2025 donors"), propose creating a new tag: pass resolve_contact_selection 1-3 concise Title Case names in suggestedTagNames (e.g. ["Repeat Donor", "Loyal Donor", "Multi-Gift 2026"]), and in your reply offer to create one of those and apply it — on their confirmation, call apply_contact_tags with createIfMissing: true. IMPORTANT: never tell the user you cannot create or apply the tag from here, or that they must do it in Settings — you CAN do both after their confirmation. Always call the tools for a tagging request instead of explaining a limitation. Do not claim a tag was applied unless apply_contact_tags actually returned success. Note: this builds an ad-hoc selection, not a Marketing Segment.',
        'For resolve_contact_selection, set ONLY the criteria fields the user explicitly stated and omit all others — never pass 0 (or any placeholder) for a field you do not mean to constrain. "this year" / "in <year>" uses the per-period fields (years plus periodAmountLte/Gte and donationCountLte/Gte), NOT the lifetime fields. "lifetime"/"total"/"ever" uses lifetimeAmountLte/Gte. Example: "tag donors who gave under $50 this year" → criteria { years: ["<current year>"], periodAmountLte: 50 } and nothing else; "donated 3+ times this year" → { years: ["<current year>"], donationCountGte: 3 }.',
        'When using a dashboard tool, call the tool first without emitting interim narration, then answer from the tool result after it returns.',
        'Never write XML, JSON, function-call syntax, tool names, tml:invoke markup, or any internal tool invocation format in the user-visible answer. If you need a tool, call the registered tool through the tool system.',
        'If a matching registered tool is not available, explain the limitation in plain language without inventing tool names or tool-call markup.',
        'Do not claim that you completed write operations unless a registered action actually did so.',
        'Do not answer general knowledge, coding, medical, legal, financial, political, entertainment, or personal advice questions unless they directly relate to using CharityStack.',
        'If a request is unrelated to CharityStack, politely say that you can only help with CharityStack dashboard workflows and redirect the user to a CharityStack task.',
        'If you are unsure whether a request is related, ask one concise clarifying question about how it relates to CharityStack.',
      ].join(' '),
    [
      dashboardCurrentTime,
      dashboardTimezoneOffset,
      normalizedDashboardTimezone,
      organizationId,
      organizationName,
    ],
  );
  const assistantContextConfig = useMemo(
    () => ({
      getContext: () => assistantContext,
    }),
    [assistantContext],
  );
  const assistantInstructionsConfig = useMemo(
    () => ({
      instruction: assistantInstructions,
    }),
    [assistantInstructions],
  );

  useAssistantContext(assistantContextConfig);
  useAssistantInstructions(assistantInstructionsConfig);

  return null;
}

export default DashboardAssistantRuntime;
